import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CafClusterDetail, CafClusterSample, CafCorpusAction, CafCorpusEntry, LabelClusterPayload, getCafCluster, getCafCorpusEntry, labelCafCluster,
  reconcileCafProjection, splitCafCluster, CafClusterSplitRequest,
} from '../../../api/cafCorpus';
import { ACTION_ORDER, FIRST_LABEL_REASON, REPLACED_LABEL_REASON, actionCopy } from '../copy';
import { AllEmails, EmailSamples } from '../EmailSamples';
import { formatAgo, formatCount } from '../format';
import { Icon } from '../Icon';
import { LabelDraft, LabelForm, draftFromEntry, draftIsValid, draftToWrite, emptyDraft, stanceField, useClaimFieldOptions } from '../LabelForm';
import { useFinalResponseSupported } from '../finalResponseSupport';
import { senderEmail } from '../lenders';
import { Drawer } from '../Overlay';
import {
  AUTO_FINISH_REASON, ReadBack, finishPublication, projectionPending, projectionSuperseded, runSave,
} from '../saveEngine';
import { useToast } from '../ToastContext';
import { ConflictWarnings, useConflictGate } from './ConflictWarnings';
import { matchesReplacementGuidance } from './guidanceComparison';

export interface LabelledGroup {
  clusterId: string;
  sender: string;
  entry: CafCorpusEntry | null;
  memberCount: number;
  /** false: the example is saved but its link to the group may not be; reload the queue rather than drop the row. */
  settled: boolean;
}

interface Props {
  clusterId: string;
  lenderOf: (sender: string) => string | null;
  onClose: () => void;
  onLabelled: (result: LabelledGroup) => void;
  onShowExample?: (entryId: string, sender: string) => void;
  /** #705 "Label it the same as that example": fill the label in from this approved example. */
  likeExample?: { entryId: string; sender: string } | null;
}

const isAction = (value: unknown): value is CafCorpusAction =>
  typeof value === 'string' && (ACTION_ORDER as string[]).indexOf(value) >= 0;

export const GroupDrawer: React.FC<Props> = (props) => <GroupReview key={props.clusterId} {...props} />;

// Only the active review renders a Drawer. Its parent keeps the exact reviewed
// snapshot and draft in memory; returning never silently rebases the approval CAS.
const GroupReview: React.FC<Props & { returning?: boolean; onApprovalResult?: (result: LabelledGroup) => void }> = ({ clusterId, lenderOf, onClose, onLabelled, onShowExample, returning = false, onApprovalResult, likeExample = null }) => {
  const toast = useToast();
  const gate = useConflictGate();
  // Final fix wave C1/C2: an older backend neither asks for nor takes the final-response setting.
  const rules = { finalResponse: useFinalResponseSupported() };
  const claimFields = useClaimFieldOptions();
  const [detail, setDetail] = useState<CafClusterDetail | null>(null);
  const [loadProblem, setLoadProblem] = useState('');
  const [finishingEarlier, setFinishingEarlier] = useState(false);
  const [draft, setDraft] = useState<LabelDraft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [saveProblem, setSaveProblem] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [splitRequest, setSplitRequest] = useState<CafClusterSplitRequest | null>(null);
  const [splitProblem, setSplitProblem] = useState('');
  const [splitBusy, setSplitBusy] = useState(false);
  const [separated, setSeparated] = useState<Array<{ id: string; count: number; operation: string; status: 'unlabelled' | 'approved' | 'approval_pending' }>>([]);
  const sourceVersion = useRef(0);
  const confirmedSamples = useRef(new Map<string, { version: number; sample: CafClusterSample }>());
  const [reviewing, setReviewing] = useState<string | null>(null);
  const mounted = useRef(true);
  const reviewState = useRef({ active: true, busy: false });
  reviewState.current = { active: reviewing === null, busy: saving || splitBusy || Boolean(splitRequest) || selectedIds.length > 0 };
  const retrySave = useRef<(() => Promise<void>) | null>(null);
  const callbacks = useRef({ onClose, onLabelled, onApprovalResult });
  callbacks.current = { onClose, onLabelled, onApprovalResult };

  const readGroup = useCallback(async () => {
    const started = sourceVersion.current;
    const fresh = await getCafCluster(clusterId);
    return { ...fresh, samples: fresh.samples.map((sample) => {
      const saved = confirmedSamples.current.get(sample.id);
      return saved && saved.version > started ? saved.sample : sample;
    }) };
  }, [clusterId]);

  useEffect(() => {
    let alive = true;
    mounted.current = true;
    (async () => {
      try {
        let fresh = await readGroup();
        if (!alive) return;
        const earlier = fresh.label_entry;
        if (fresh.status === 'unlabelled' && earlier && earlier.active && earlier.version >= 1) {
          // Saved before, but the link to this group never finished. Finish it now, quietly.
          setFinishingEarlier(true);
          try {
            await reconcileCafProjection(earlier.id, { sender: fresh.sender, expected_version: earlier.version, reason: AUTO_FINISH_REASON });
            fresh = await readGroup();
          } catch {
            // Fall through to the form; staff can still save.
          }
          if (!alive) return;
          setFinishingEarlier(false);
          if (fresh.status === 'labelled') {
            const result = { clusterId, sender: fresh.sender, entry: null, memberCount: fresh.member_count, settled: true };
            callbacks.current.onLabelled(result);
            callbacks.current.onApprovalResult?.(result);
            toast.saved('This group was already labelled. It is now in Approved examples.');
            callbacks.current.onClose();
            return;
          }
        }
        if (!alive) return;
        setDetail(fresh);
        if (fresh.split_pending) {
          const { task_ids, expected_etag, operation_id, reason } = fresh.split_pending.request;
          setSplitRequest({ task_ids, expected_etag, operation_id, reason });
          setSelectedIds(task_ids);
        }
        const older = fresh.label_entry;
        if (older && isAction(older.correct_action)) {
          const action = older.correct_action;
          setDraft((prev) => ({ ...prev, action, why: older.note ?? '', finalResponse: older.final_response_stance ?? prev.finalResponse }));
        }
      } catch (err) {
        if (!alive) return;
        if ((err as { response?: { status?: number } })?.response?.status === 404) {
          toast.info('This group no longer exists. It may have been regrouped overnight.');
          callbacks.current.onClose();
          return;
        }
        const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
        setLoadProblem(code === 'ambiguous_cluster_id'
          ? 'Two groups share this ID. Ask support to repair them before reviewing.'
          : "Couldn't load this group. Close it and try again.");
      }
    })();
    return () => { alive = false; mounted.current = false; };
  }, [clusterId, readGroup, toast]);

  // Update displayed facts without advancing the reviewed approval etag. Saves
  // beat reads already in flight; later server reads remain authoritative.
  useEffect(() => {
    const receive = (event: Event) => {
      const fresh = (event as CustomEvent<CafClusterSample>).detail;
      sourceVersion.current += 1;
      confirmedSamples.current.set(fresh.id, { version: sourceVersion.current, sample: fresh });
      setDetail((previous) => previous ? { ...previous,
        samples: previous.samples.map((sample) => sample.id === fresh.id ? fresh : sample),
      } : previous);
    };
    window.addEventListener('caf-final-response-refreshed', receive);
    return () => window.removeEventListener('caf-final-response-refreshed', receive);
  }, []);

  // #705: "Label it the same as that example" fills the label in once the group has loaded.
  // Staff still read the emails and press Save; nothing is saved for them.
  const [likeState, setLikeState] = useState<'none' | 'filled' | 'failed'>('none');
  const groupLoaded = Boolean(detail);
  const likeEntryId = likeExample?.entryId;
  const likeSender = likeExample?.sender;
  useEffect(() => {
    if (!groupLoaded || !likeEntryId || !likeSender) return undefined;
    let alive = true;
    getCafCorpusEntry(likeEntryId, likeSender).then(
      (entry) => { if (alive) { setDraft(draftFromEntry(entry)); setLikeState('filled'); } },
      () => { if (alive) setLikeState('failed'); },
    );
    return () => { alive = false; };
  }, [groupLoaded, likeEntryId, likeSender]);

  const older = detail?.label_entry ?? null;
  const reason = older ? REPLACED_LABEL_REASON : FIRST_LABEL_REASON;

  const save = async () => {
    if (!mounted.current || !reviewState.current.active || reviewState.current.busy || !detail || !detail.member_count || !draftIsValid(draft, rules)) return;
    const write = draftToWrite(draft, claimFields, undefined, rules);
    const checked = await gate.confirmOrWarn({ source: { kind: 'cluster', id: clusterId }, sender: detail.sender, action: write.action, final_response_stance: write.final_response_stance });
    if (!checked.proceed || !mounted.current) return;
    const expected = older?.version ?? 0;
    const payload: LabelClusterPayload = {
      action: write.action,
      note: write.note,
      extraction_schema: write.extraction_schema,
      ...stanceField(write),
      ...(write.archive_override_refs !== undefined ? { archive_override_refs: write.archive_override_refs } : {}),
      ...(write.categoryOverride ? { categoryOverride: write.categoryOverride } : {}),
      expected_version: expected,
      expected_cluster_etag: detail._etag,
      ...(checked.warnings.length ? { acknowledged_warnings: checked.warnings } : {}),
      ...(older && older.version === 0 && older.compare_token ? { reviewed_token: older.compare_token } : {}),
      reason,
    };
    setSaving(true);
    setSaveProblem('');
    const result = await runSave<CafCorpusEntry>(() => labelCafCluster(clusterId, payload), {
      readBack: async (): Promise<ReadBack<CafCorpusEntry>> => {
        const fresh = await getCafCluster(clusterId);
        const saved = fresh.label_entry;
        if (saved?.version === expected + 1) {
          const canonical = await getCafCorpusEntry(saved.id, fresh.sender);
          return canonical.version === expected + 1 && matchesReplacementGuidance(canonical, payload)
            ? { state: 'landed', value: canonical } : { state: 'unknown' };
        }
        if (!saved || saved.version === expected) return { state: 'not_landed' };
        return { state: 'unknown' };
      },
      finish: (entry) => finishPublication(entry),
    });
    if (!mounted.current || !reviewState.current.active) return;
    setSaving(false);
    if (!result.ok) {
      setSaveProblem(result.message);
      toast.failed(result.message, () => { void retrySave.current?.(); });
      return;
    }
    const entry = result.value;
    const settled = Boolean(entry) && !projectionPending(entry as CafCorpusEntry) && !projectionSuperseded(entry as CafCorpusEntry);
    const approval = { clusterId, sender: detail.sender, entry, memberCount: detail.member_count, settled };
    onLabelled(approval);
    onApprovalResult?.(approval);
    if (entry && projectionSuperseded(entry)) {
      toast.saved('Saved. Someone else changed this example at the same time, so their version is the one in use.');
    } else if (!settled) {
      toast.saved('Saved. The group’s approval details could not yet be confirmed.');
    } else {
      const who = lenderOf(detail.sender) ?? senderEmail(detail.sender);
      toast.saved(
        `Saved. ${formatCount(detail.member_count)} emails from ${who} are now covered by an approved example.`,
        entry && onShowExample ? { label: 'Show it', onClick: () => onShowExample(entry.id, entry.sender) } : undefined,
      );
    }
    onClose();
  };

  retrySave.current = save;

  const selectEmail = (id: string) => setSelectedIds((ids) => ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id]);
  const canEditMembership = detail?.status === 'unlabelled' && !older && !detail?.membership_label_reserved;
  const split = async () => {
    if (!mounted.current || !reviewState.current.active || !detail || splitBusy || saving || (!splitRequest && (!canEditMembership || !detail._etag || !selectedIds.length || selectedIds.length >= detail.member_count))) return;
    const request = splitRequest ?? {
      task_ids: selectedIds, expected_etag: detail._etag ?? '', operation_id: window.crypto.randomUUID(),
      reason: 'Separated mismatched emails before approving the group',
    };
    setSplitRequest(request);
    setSplitBusy(true);
    setSplitProblem('');
    try {
      const result = await splitCafCluster(clusterId, request);
      if (!mounted.current) return;
      if (result.status !== 'complete' || !result.separated_cluster?.id) throw new Error('Split is pending');
      const fresh = await readGroup();
      if (!mounted.current) return;
      setDetail(fresh);
      setSeparated((previous) => previous.some((item) => item.operation === request.operation_id) ? previous : [...previous,
        { id: result.separated_cluster.id, count: request.task_ids.length, operation: request.operation_id, status: 'unlabelled' }]);
      setSelectedIds([]);
      setSplitRequest(null);

      // Refresh the queue counts and include the new reviewable group.
      onLabelled({ clusterId, sender: fresh.sender, entry: null, memberCount: fresh.member_count, settled: false });
    } catch (error) {
      if (!mounted.current) return;
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409 || status === 400) {
        try {
          const fresh = await readGroup();
          if (!mounted.current) return;
          setDetail(fresh);
          if (fresh.split_pending) {
            const { task_ids, expected_etag, operation_id, reason } = fresh.split_pending.request;
            setSplitRequest({ task_ids, expected_etag, operation_id, reason });
            setSelectedIds(task_ids);
          } else { setSplitRequest(null); setSelectedIds([]); }
        } catch { /* Keep the original operation so retry remains safe. */ }
        setSplitProblem('This group changed or the selection cannot be split. Review the refreshed emails, then try again.');
      } else {
        setSplitProblem('The split is not confirmed yet. Retry to finish the same split safely.');
      }
    } finally { if (mounted.current) setSplitBusy(false); }
  };

  const title = detail ? (lenderOf(detail.sender) ?? senderEmail(detail.sender)) : 'Group of similar emails';
  const subtitle = detail
    ? `${senderEmail(detail.sender)} · ${formatCount(detail.member_count)} similar emails · first seen ${formatAgo(detail.created_at).toLowerCase()}`
    : undefined;

  if (reviewing) return <GroupReview key={reviewing} clusterId={reviewing} lenderOf={lenderOf}
    onClose={() => setReviewing(null)} onLabelled={onLabelled} onApprovalResult={(result) => {
      if (result.clusterId === reviewing) {
        setSeparated((previous) => previous.map((item) => item.id === result.clusterId
          ? { ...item, status: result.settled ? 'approved' : 'approval_pending' } : item));
      }
    }} onShowExample={onShowExample} returning />;

  const navigationBusy = saving || splitBusy || (returning && Boolean(splitRequest));
  return (
    <Drawer eyebrow="Group of similar emails" title={title} subtitle={subtitle} onClose={onClose} busy={navigationBusy}
      footer={detail && (
        <>
          {saveProblem && <div className="cur-alert cur-alert--error" role="alert"><span className="cur-grow">{saveProblem}</span></div>}
          <span className="cur-auto-reason cur-grow"><Icon name="clock" size="sm" />History will record this as &quot;{reason}&quot;.</span>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose} disabled={navigationBusy}>{returning ? 'Back to original group' : 'Cancel'}</button>
          <button type="button" className="ui-btn ui-btn--primary ui-btn--md" onClick={() => { void save(); }}
            disabled={saving || splitBusy || Boolean(splitRequest) || selectedIds.length > 0 || !detail.member_count || !draftIsValid(draft, rules)}>
            {saving ? <><span className="cur-spin" aria-hidden="true" />Saving…</> : gate.conflicts.length ? 'Save anyway' : 'Save as approved example'}
          </button>
        </>
      )}>
      {returning && !detail && <button type="button" className="cur-linkish" disabled={navigationBusy} onClick={onClose}>Back to original group</button>}
      {finishingEarlier && <p className="cur-hint">Finishing an earlier save…</p>}
      {loadProblem && <div className="cur-alert cur-alert--error" role="alert">{loadProblem}</div>}
      {!detail && !loadProblem && !finishingEarlier && <p className="cur-hint">Loading…</p>}
      {detail && (
        <>
          <section className="cur-membership-review">
            <h3 className="cur-sec-title">Review which emails belong together</h3>
            <p className="cur-hint">Read the emails and attachments. The same covering email can come with different PDFs. Separate genuine outliers before approving the remaining group; correct a false final-response flag inside the reader when the email belongs here.</p>
            {(canEditMembership || splitRequest) ? <>
              <p className="cur-hint">Selected emails move to a new unlabelled review group. Leave at least one email in this group.</p>
              <div className="cur-sample-actions">
                <span>{selectedIds.length} selected · {detail.member_count - selectedIds.length} remaining</span>
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" onClick={() => { void split(); }}
                  disabled={saving || splitBusy || (!splitRequest && (!detail._etag || !selectedIds.length || selectedIds.length >= detail.member_count))}>
                  {splitBusy ? 'Separating…' : splitRequest ? 'Retry split' : 'Separate selected emails'}
                </button>
              </div>
            </> : <p className="cur-hint">These emails cannot be separated because this group is already labelled or reserved by an earlier approval. Membership editing is unavailable for this group.</p>}
            {splitProblem && <p role="alert">{splitProblem}</p>}
            {separated.map((item) => <div className="cur-alert cur-alert--info cur-split-result" role="status" key={item.operation}>
              <span className="cur-grow">{formatCount(item.count)} {item.count === 1 ? 'email separated' : 'emails separated'}{item.status === 'approved' ? '. Separated group is now approved.' : item.status === 'approval_pending' ? '. Separated group approval is not yet confirmed.' : ' into a new unlabelled group.'} Your draft for the remaining group is kept.</span>
              <button type="button" className="cur-linkish" disabled={saving || splitBusy || Boolean(splitRequest)}
                onClick={() => setReviewing(item.id)}>Review separated group</button>
            </div>)}
          </section>
          <section>
            <h3 className="cur-sec-title">Sample emails <span className="cur-muted">({detail.samples.length})</span></h3>
            <EmailSamples samples={detail.samples} allowMarkFinal selectedIds={selectedIds}
              onSelect={canEditMembership ? selectEmail : undefined} selectionDisabled={saving || splitBusy || Boolean(splitRequest)} />
            <div style={{ marginTop: 10 }}><AllEmails key={detail._etag} clusterId={clusterId} total={detail.member_count} allowMarkFinal
              selectedIds={selectedIds} onSelect={canEditMembership ? selectEmail : undefined}
              selectionDisabled={saving || splitBusy || Boolean(splitRequest)} /></div>
          </section>
          <section>
            <h3 className="cur-sec-title">Approve the remaining pattern</h3>
            <p className="cur-hint">Approval teaches this email pattern and how it should be handled. It does not rewrite each member's final-response flag. Assessments on the loaded emails are review cues, not an approval requirement.</p>
            {detail.membership_label_reserved && !older && (
              <p className="cur-hint">An earlier approval attempt reserved these emails. Save again to finish approving this group.</p>
            )}
            {older && (
              <div className="cur-alert cur-alert--info" role="note">
                This group already has an older label ({actionCopy(older.correct_action).label}). Saving replaces it.
              </div>
            )}
            {likeState === 'filled' && (
              <div className="cur-alert cur-alert--info" role="note">
                Filled in from the approved example this group looks like. Check the emails belong together, then save.
              </div>
            )}
            {likeState === 'failed' && (
              <div className="cur-alert cur-alert--warn" role="note">
                Couldn&apos;t fill this in from the example. Choose what should happen yourself.
              </div>
            )}
            <LabelForm idPrefix={`group-${clusterId}`} value={draft} onChange={setDraft} claimFields={claimFields} disabled={saving || splitBusy || Boolean(splitRequest)} />
            <ConflictWarnings conflicts={gate.conflicts} proposal={{ action: draft.action, final_response_stance: draft.finalResponse }} />
          </section>
        </>
      )}
    </Drawer>
  );
};
